import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { GetPostsResponseMessage, PostsSlice, SetPostReactionState, SavePostComment, PostData, ApprovePostRequest } from '../../../interfaces/post.interface';
import { BrithDaysPayload } from '../../../interfaces/birthDay.interface';
import { cleanDuplicates } from '../../../utils/data';

export interface PostSlice {
    loadingPosts: boolean;
    savePostLoading: boolean;
    postError: string;
    loadingRecognitions: boolean;
    recognitionsError: string;
    posts: PostsSlice;
    recognitions: PostsSlice;
    quickactions: PostsSlice;
    birthdaysYearsRequested: number[];
    birthdaysData: BrithDaysPayload | null;
    anniversariesData: (BrithDaysPayload & {isAllData?: boolean}) | null;
    birthdaysDataCurrent: (BrithDaysPayload & {isAllData?: boolean}) | null;
}


const initialState: PostSlice = {
    loadingPosts: false,
    savePostLoading: false,
    postError: "",
    loadingRecognitions: false,
    recognitionsError: "",
    posts: {data: null},
    recognitions: {data: null},
    quickactions: {data: null},
    birthdaysYearsRequested: [],
    birthdaysData: null,
    anniversariesData: null,
    birthdaysDataCurrent: null,
}

export const postSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setSavePostsLoading: (state, {payload}: PayloadAction<boolean>) => {
            state.savePostLoading = payload;
        },
        setPostsLoading: (state, {payload}: PayloadAction<boolean>) => {
            state.loadingPosts = payload;
        },
        setPostError: (state, {payload}: PayloadAction<string>) => {
            state.postError = payload;
        },
        setRecognitionsLoading: (state, {payload}: PayloadAction<boolean>) => {
            state.loadingRecognitions = payload;
        },
        setRecognitionError: (state, {payload}: PayloadAction<string>) => {
            state.recognitionsError = payload;
        },
        setResetPost: (state, {payload}: PayloadAction<string>) => {
            if (payload === "quickactions") {
                state.quickactions.data = [];
                state.quickactions.nextPageB64 = null;
            } else if (payload === "recognition") {
                state.recognitions.data = [];
                state.recognitions.nextPageB64 = null;
            } else if (["post", "unapprovedPost"].includes(payload)) {
                state.posts.data = [];
                state.posts.nextPageB64 = null;
            }

        },
        setPostsResponse: (state, {payload}: PayloadAction<{postType: string, response: GetPostsResponseMessage}>) => {

            if (payload.postType === "quickactions") {
                const prevItems = state.quickactions.data ?? []
                const items = payload?.response?.Items ?? []
                state.quickactions.data = cleanDuplicates([...prevItems || [], ...items], 'postId');
                state.quickactions.nextPageB64 = payload.response.nextPageB64;
            } else if (payload.postType === "recognition") {
                const prevItems = state.recognitions.data ?? []
                const items = payload?.response?.Items ?? []
                state.recognitions.data = cleanDuplicates([...prevItems || [], ...items], 'postId');
                state.recognitions.nextPageB64 = payload.response.nextPageB64;
            } else if (["post", "unapprovedPost"].includes(payload.postType)) {
                if (state.posts.postType !== payload.postType) {
                    state.posts.data = [];
                }
                const prevItems = state.posts.data ?? []
                const items = payload?.response?.Items ?? []
                state.posts.data = cleanDuplicates([...prevItems || [], ...items], 'postId');
                state.posts.nextPageB64 = payload.response.nextPageB64;
            }
        },
        setApprovedPosts: (state, {payload}: PayloadAction<{id: string, newStatus: ApprovePostRequest['approvalStatus']}>) => {
            const prevItems = [...(state.posts.data ?? [])];
            if (payload.newStatus === "approved") {
                state.posts.data = prevItems.filter((p: PostData) => p.postId !== payload.id);
            } else {
                state.posts.data = prevItems.map((p: PostData) => ({...p, approvalStatus: payload.newStatus}));
            }
        },



        setBirthdaysYearRequested: (state, {payload}: PayloadAction<number>) => {
            const prevItems = state.birthdaysYearsRequested ?? []
            prevItems.push(payload);
            state.birthdaysYearsRequested = prevItems;
        },
        setBirthdaysData: (state, {payload}: PayloadAction<BrithDaysPayload>) => {
            const isCurrent = payload.isCurrent === true;
            const prevItems = isCurrent 
                                ? state.birthdaysDataCurrent?.data ?? [] 
                                : state.birthdaysData?.data ?? []
            const newData = cleanDuplicates([...prevItems, ...payload.data], "CodIdentificador")
                .sort((a, b) => {
                    const today = new Date();
                    const currentMonth = today.getMonth() + 1; // Mes actual (1-12)
                    const currentDay = today.getDate(); // Día actual
                
                    const getAdjustedDate = (dateStr: string) => {
                        const date = new Date(dateStr);
                        const month = date.getMonth() + 1;
                        const day = date.getDate();
                
                        if (isCurrent) {
                            if (month < currentMonth || (month === currentMonth && day < currentDay)) {
                                return new Date(2024, month - 1, day).getTime();
                            } else {
                                return new Date(2023, month - 1, day).getTime();
                            }
                        } else {
                            return new Date(2023, month - 1, day).getTime();
                        }
                    };
                    const adjustedA = getAdjustedDate(a.Aniversario);
                    const adjustedB = getAdjustedDate(b.Aniversario);
                    return adjustedA - adjustedB;
                });
            if (payload.isCurrent) {
                state.birthdaysDataCurrent = {
                    ...payload, 
                    data: newData as any,
                    isAllData: payload.data.length < 10
                };
                return;
            }
            state.birthdaysData = {...payload, data: newData as any};
        },
        setAnniversaryData: (state, {payload}: PayloadAction<BrithDaysPayload>) => {
            const prevItems = state.anniversariesData?.data ?? []
            const newData = cleanDuplicates([...prevItems, ...payload.data], "CodIdentificador")
                .sort((a, b) => {
                    const getAdjustedDate = (dateStr: string) => {
                        const date = new Date(dateStr);
                        const today = new Date();
                        const currentYear = today.getFullYear();
                        const anniversaryThisYear = new Date(currentYear, date.getMonth(), date.getDate());
                        if (anniversaryThisYear < today) {
                            return new Date(currentYear + 1, date.getMonth(), date.getDate()).getTime();
                        }
                        
                        return anniversaryThisYear.getTime();
                    };
                    
                    const adjustedA = getAdjustedDate(a.Aniversario);
                    const adjustedB = getAdjustedDate(b.Aniversario);
                    return adjustedA - adjustedB;
                });
            state.anniversariesData = {...payload, data: newData as any, isAllData: payload.data.length < 10};
        },

        setPostReaction: ({posts: {data}}: PostSlice, {payload}: PayloadAction<SetPostReactionState>) => {
            if (!data) return;
            const index = data.findIndex((p) => p['orderTimestamp-ID'] === payload['orderTimestamp-ID']);
            if (index === -1)  return;
            const postData = data[index];
            postData.hasLiked = payload.hasLiked;
            postData.reactions.like = (postData.reactions?.like || 0) + (payload.hasLiked ? 1 : -1);
            data[index] = postData;            
        },
        setPostComment: ({posts: {data}}: PostSlice, {payload}: PayloadAction<SavePostComment>) => {
            if (!data) return;
            const index = data.findIndex((p) => p['orderTimestamp-ID'] === payload['orderTimestamp-ID']);
            if (index === -1)  return;
            const postData = data[index];
            postData.comments += 1
            data[index] = postData;            
        },
    }
})


export const { 
    setAnniversaryData,
    setResetPost,
    setPostsLoading,
    setApprovedPosts,
    setPostError,
    setRecognitionsLoading,
    setRecognitionError,
    setPostsResponse,
    setBirthdaysYearRequested,
    setBirthdaysData,
    setSavePostsLoading,
    setPostReaction,
    setPostComment,
} = postSlice.actions
